import { Component, inject, OnInit, viewChild } from '@angular/core';
import { NgClass } from '@angular/common';
import { Button } from 'primeng/button';
import { Popover } from 'primeng/popover';
import { Skeleton } from 'primeng/skeleton';
import { Tag } from 'primeng/tag';
import {
    AI_ASSIST_ICON,
    AiAssistService,
    VENDOR_DASHBOARD_URLS,
    VendorBalance,
    VendorBalanceStatus,
} from '@/app/services/ai-assist.service';

/**
 * Topbar indicator for the AI Assist service: a traffic-light dot on the action
 * button plus a popover with service health and per-vendor credit balances.
 * Split out of AppTopbar, which was carrying this whole panel — and its label /
 * severity mapping — inline.
 */
@Component({
    selector: 'app-ai-assist-status',
    standalone: true,
    imports: [NgClass, Button, Popover, Skeleton, Tag],
    templateUrl: './ai-assist-status.html',
    host: { class: 'contents' },
})
export class AiAssistStatus implements OnInit {
    protected readonly aiAssist = inject(AiAssistService);
    protected readonly aiIcon = AI_ASSIST_ICON;

    private readonly popover = viewChild.required(Popover);

    ngOnInit(): void {
        // Colour the status dot on load without waiting for a click.
        void this.aiAssist.refreshHealth();
        void this.aiAssist.refreshVendorBalances();
    }

    /** Re-probe health + balances, then open the detail popover. */
    protected open(event: Event): void {
        this.refresh();
        this.popover().toggle(event);
    }

    protected refresh(): void {
        void this.aiAssist.refreshHealth();
        void this.aiAssist.refreshVendorBalances(true);
    }

    /**
     * Tailwind background for the indicator dot. Combines service status with
     * vendor credits: a critical balance is as urgent as the service being
     * down (red); a low balance shows amber even when the service is fine.
     */
    protected statusDotClass(): string {
        const balances = this.aiAssist.vendorBalances();
        if (this.aiAssist.status() === 'down' || balances?.any_critical) {
            return 'bg-red-500';
        }
        if (this.aiAssist.status() === 'degraded' || balances?.any_low) {
            return 'bg-yellow-500';
        }
        if (this.aiAssist.status() === 'ok') {
            return 'bg-green-500';
        }
        return 'bg-gray-400';
    }

    /** PrimeNG Tag severity for a single vendor's balance status. */
    protected balanceSeverity(
        status: VendorBalanceStatus,
    ): 'success' | 'warn' | 'danger' | 'secondary' {
        switch (status) {
            case 'ok':
                return 'success';
            case 'low':
                return 'warn';
            case 'critical':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    /** Compact balance label, e.g. "1,234 credits", "$12.34", "n/a", "—". */
    protected balanceLabel(v: VendorBalance): string {
        if (v.status === 'unconfigured') return 'n/a';
        if (v.balance === null) return '—';
        if (v.unit === 'usd') return `$${v.balance.toFixed(2)}`;
        return `${v.balance.toLocaleString()} ${v.unit}`;
    }

    /** Human-readable vendor name for the credits list. */
    protected vendorLabel(vendor: string): string {
        switch (vendor) {
            case 'flux':
                return 'FLUX';
            case 'recraft':
                return 'Recraft';
            case 'grok':
                return 'Grok';
            default:
                return vendor;
        }
    }

    /** Vendor billing-console URL for the external-link icon, or null. */
    protected dashboardUrl(vendor: string): string | null {
        return VENDOR_DASHBOARD_URLS[vendor] ?? null;
    }

    /** PrimeNG Tag severity matching the service status. */
    protected statusSeverity(): 'success' | 'warn' | 'danger' | 'secondary' {
        switch (this.aiAssist.status()) {
            case 'ok':
                return 'success';
            case 'degraded':
                return 'warn';
            case 'down':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    /** Human-readable status label for the popover header tag. */
    protected statusLabel(): string {
        switch (this.aiAssist.status()) {
            case 'ok':
                return 'Operational';
            case 'degraded':
                return 'Degraded';
            case 'down':
                return 'Unavailable';
            default:
                return 'Checking…';
        }
    }
}
